<template>
  <div class="spot-qr-dialog">
    <canvas ref="canvasRef" class="qr-canvas" :class="{ 'is-square': handshake }" />
    <canvas v-if="handshake && !minimal" ref="backCanvasRef" class="qr-canvas is-square" />
    <canvas v-if="handshake && !minimal" ref="playBackCanvasRef" class="qr-canvas is-square" />
    <div v-if="!minimal" class="qr-actions">
      <template v-if="handshake">
        <button class="qr-download-btn" @click="saveCanvas(canvasRef, 'qr')">
          {{ localize('master.qr.print.front') }}
        </button>
        <button class="qr-download-btn" :disabled="pdfBusy" @click="saveFrontPdf">
          {{ localize('master.qr.print.frontPdf') }}
        </button>
        <button class="qr-download-btn" @click="saveCanvas(backCanvasRef, 'qr-back')">
          {{ localize('master.qr.print.back') }}
        </button>
        <button class="qr-download-btn" :disabled="pdfBusy" @click="saveBackPdf">
          {{ localize('master.qr.print.backPdf') }}
        </button>
        <button class="qr-download-btn" @click="saveCanvas(playBackCanvasRef, 'qr-back-play')">
          {{ localize('master.qr.print.backPlay') }}
        </button>
        <button class="qr-download-btn" :disabled="pdfBusy" @click="savePlayBackPdf">
          {{ localize('master.qr.print.backPlayPdf') }}
        </button>
      </template>
      <template v-else>
        <button class="qr-download-btn" @click="saveCanvas(canvasRef, 'qr')">
          {{ localize(downloadLabel ?? 'spot.qr.download') }}
        </button>
        <button class="qr-download-btn" :disabled="pdfBusy" @click="saveFrontPdf">
          {{ localize('spot.qr.downloadPdf') }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import { CHESS_PIECE_SVGS } from '@/views/composables/constants/components/gamChessboard.constants';
import { backRankFromNumber } from '@/views/main/handshakes/utils/handshake-game';
import QRCode from 'qrcode';
import { onMounted, ref } from 'vue';

const props = defineProps<{
  spotName: string;
  spotId?: string;
  url?: string;
  minimal?: boolean;
  straight?: boolean;
  handshake?: boolean;
  downloadLabel?: string;
}>();

const canvasRef = ref<HTMLCanvasElement>();
const backCanvasRef = ref<HTMLCanvasElement>();
const playBackCanvasRef = ref<HTMLCanvasElement>();
const pdfBusy = ref(false);
/** The preview is drawn at this many device pixels per design pixel. */
const PREVIEW_SCALE = 4;
/** A printed page is one point per design pixel, so this is 576 dpi. */
const PRINT_SCALE = 8;
let qrModules: boolean[][] = [];

const SQUARE_SIZE = 20;
const BOARD_SQUARES = 8;
const QR_SQUARES = BOARD_SQUARES - 2;
const QR_SIZE = QR_SQUARES * SQUARE_SIZE;
const BOARD_SIZE = BOARD_SQUARES * SQUARE_SIZE;
const BOARD_BORDER = 4;
const COORD_GAP = 14;
const CARD_PADDING = 4;
const CARD_RADIUS = 12;
const CANVAS_PADDING = 24;
const TEXT_GAP = 28;

/* The dialog frame is a fixed cost around the slip: the modal caps itself at 88vw
   on mobile (500px above that) and the panel plus canvas wrapper each add padding
   on both sides. The canvas keeps its 4x backing store either way, so drawing it
   into fewer CSS pixels only makes it sharper. */
const DIALOG_MAX_WIDTH = 500;
const DIALOG_MOBILE_WIDTH_RATIO = 0.88;
const DIALOG_FRAME_WIDTH = 80;

function fittedWidth(naturalWidth: number): number {
  const modalWidth = Math.min(window.innerWidth * DIALOG_MOBILE_WIDTH_RATIO, DIALOG_MAX_WIDTH);
  return Math.min(naturalWidth, Math.floor(modalWidth - DIALOG_FRAME_WIDTH));
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawChessboardFrame(ctx: CanvasRenderingContext2D, boardX: number, boardY: number) {
  for (let row = 0; row < BOARD_SQUARES; row++) {
    for (let col = 0; col < BOARD_SQUARES; col++) {
      const isEdge = row === 0 || row === BOARD_SQUARES - 1 || col === 0 || col === BOARD_SQUARES - 1;
      if (!isEdge) continue;

      const isDark = (row + col) % 2 === 1;
      ctx.fillStyle = isDark ? '#000000' : '#ffffff';
      ctx.fillRect(boardX + col * SQUARE_SIZE, boardY + row * SQUARE_SIZE, SQUARE_SIZE, SQUARE_SIZE);
    }
  }

  ctx.strokeStyle = '#000000';
  ctx.lineWidth = BOARD_BORDER;
  const half = BOARD_BORDER / 2;
  ctx.strokeRect(boardX - half, boardY - half, BOARD_SIZE + BOARD_BORDER, BOARD_SIZE + BOARD_BORDER);
}

function drawCoordinates(ctx: CanvasRenderingContext2D, boardX: number, boardY: number) {
  const files = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  const ranks = ['8', '7', '6', '5', '4', '3', '2', '1'];

  ctx.fillStyle = '#333333';
  ctx.font = "bold 7px 'Hanken Grotesk', sans-serif";
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const mid = BOARD_BORDER + COORD_GAP / 2 + CARD_PADDING / 2;

  for (let i = 0; i < BOARD_SQUARES; i++) {
    const cx = boardX + i * SQUARE_SIZE + SQUARE_SIZE / 2;

    ctx.fillText(files[i], cx, boardY + BOARD_SIZE + mid);

    ctx.save();
    ctx.translate(cx, boardY - mid);
    ctx.rotate(Math.PI);
    ctx.fillText(files[i], 0, 0);
    ctx.restore();
  }

  for (let i = 0; i < BOARD_SQUARES; i++) {
    const cy = boardY + i * SQUARE_SIZE + SQUARE_SIZE / 2;

    ctx.fillText(ranks[i], boardX - mid, cy);

    ctx.save();
    ctx.translate(boardX + BOARD_SIZE + mid, cy);
    ctx.rotate(Math.PI);
    ctx.fillText(ranks[i], 0, 0);
    ctx.restore();
  }
}

function drawQrModules(ctx: CanvasRenderingContext2D, modules: boolean[][], x: number, y: number, size: number) {
  const moduleCount = modules.length;
  const moduleSize = size / moduleCount;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, size, size);

  ctx.fillStyle = '#000000';
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (modules[row][col]) {
        ctx.fillRect(x + col * moduleSize, y + row * moduleSize, moduleSize + 0.5, moduleSize + 0.5);
      }
    }
  }
}

function drawHandshakeIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  const scale = size / 16;
  ctx.save();
  ctx.translate(cx - size / 2, cy - size / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(7, 3.5);
  ctx.lineTo(7, 12.5);
  ctx.lineTo(0, 8);
  ctx.closePath();
  ctx.moveTo(6, 5.33);
  ctx.lineTo(6, 10.67);
  ctx.lineTo(1.85, 8);
  ctx.closePath();
  ctx.fill('evenodd');
  ctx.beginPath();
  ctx.moveTo(9, 3.5);
  ctx.lineTo(9, 12.5);
  ctx.lineTo(16, 8);
  ctx.closePath();
  ctx.moveTo(10, 5.33);
  ctx.lineTo(10, 10.67);
  ctx.lineTo(14.15, 8);
  ctx.closePath();
  ctx.fill('evenodd');
  ctx.restore();
}

const CARD_ANGLE_DEG = -10;

function drawCardWithContent(
  ctx: CanvasRenderingContext2D,
  cardWidth: number,
  cardHeight: number,
  modules: boolean[][],
) {
  drawRoundedRect(ctx, -cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, CARD_RADIUS);
  ctx.fillStyle = '#ffffff';
  ctx.fill();

  const innerX = -cardWidth / 2 + CARD_PADDING + COORD_GAP + BOARD_BORDER;
  const innerY = -cardHeight / 2 + CARD_PADDING + COORD_GAP + BOARD_BORDER;

  drawChessboardFrame(ctx, innerX, innerY);
  drawCoordinates(ctx, innerX, innerY);
  drawQrModules(ctx, modules, innerX + SQUARE_SIZE, innerY + SQUARE_SIZE, QR_SIZE);
}

const TOP_OVERFLOW = 12;
/** The handshake slip trades a little QR size for a band wide enough to write a code in by hand. */
const HANDSHAKE_CARD_SCALE = 0.88;
/** Sits lower than the other variants so the square top edge clips less of the board. */
const HANDSHAKE_TOP_OVERFLOW = 9;
/** One flat print pink. A two-stop gradient is too fine to hold once the page is converted to ink. */
const HANDSHAKE_TITLE_PINKS = ['#F04B8A', '#F04B8A'];
/** Room added above the board for the title. The board still covers its lower part. */
const HANDSHAKE_TITLE_BAND = 52;
const HANDSHAKE_TITLE_SIZE = 64;
const HANDSHAKE_TITLE_INSET = 14;
const HANDSHAKE_TITLE_TOP = 12;
/** Small enough that the hand stays the 14px the pink badge drew on its 56px ring. */
const HANDSHAKE_BADGE_SIZE = (56 * 48) / 72;
/** 8px corners and an 18px mark on the profile's 48px button. */
const HANDSHAKE_MARK_RADIUS = 8 / 48;
const HANDSHAKE_MARK_ICON = 18 / 48;
/** The handshake sheet rounds its 420px square by 36px. Both sides of the print keep that share of their width. */
const SHEET_RADIUS_RATIO = 36 / 420;
/** The sheet's two greys, far enough apart that the checker survives in ink. The darker one sits on a1. */
const SHEET_SQUARES = ['#101010', '#3A3A3A'];
const CROWN_PATHS = [
  'M1.37283 13.3842C2.32282 13.3842 3.09293 12.6141 3.09293 11.6641C3.09293 10.7141 2.32282 9.94402 1.37283 9.94402V13.3842Z',
  'M1.37283 23.9915V16.1077H3.1642C3.1642 19.8205 6.16806 22.8319 9.87787 22.8447H9.89987C13.5937 22.8319 16.5872 19.8464 16.6129 16.1557V16.1077H20.1965C20.1965 19.8205 23.2003 22.8319 26.9101 22.8447H26.9321C30.6259 22.8319 33.6194 19.8464 33.6452 16.1557V16.1077H35.5313V23.9915H1.37283Z',
  'M35.5313 9.94654V13.3817C35.5002 13.3834 35.4689 13.3842 35.4374 13.3842C34.4874 13.3842 33.7172 12.6141 33.7172 11.6641C33.7172 10.7141 34.4874 9.94402 35.4374 9.94402C35.4689 9.94402 35.5002 9.94486 35.5313 9.94654Z',
  'M18.4051 9.94402C17.4551 9.94402 16.685 10.7141 16.685 11.6641C16.685 12.6141 17.4551 13.3842 18.4051 13.3842C19.3551 13.3842 20.1252 12.6141 20.1252 11.6641C20.1252 10.7141 19.3551 9.94402 18.4051 9.94402Z',
  'M1.37219 25.5319H35.5313V26.056H1.37219V25.5319Z',
];
const CROWN_VIEWBOX = 36;
const BACK_CROWN_DISC = 44;
const BACK_CROWN_SIZE = 28;
/** Same baseline as gambits.com on the front, so both sides share one footer. */
const BACK_URL_INSET = 14;

function renderCanvas(modules: boolean[][], canvas = canvasRef.value, scale = PREVIEW_SCALE) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const preview = canvas === canvasRef.value;

  const cardContentSize = BOARD_SIZE + BOARD_BORDER * 2 + COORD_GAP * 2;
  const cardWidth = cardContentSize + CARD_PADDING * 2;
  const cardHeight = cardContentSize + CARD_PADDING * 2;

  const maxAngleRad = (Math.abs(CARD_ANGLE_DEG) * Math.PI) / 180;
  const rotatedCardW = Math.cos(maxAngleRad) * cardWidth + Math.sin(maxAngleRad) * cardHeight;

  const sizeCanvas = (width: number, height: number) => {
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    if (preview) canvas.style.width = `${fittedWidth(width)}px`;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    return { width, height };
  };

  if (props.minimal) {
    const effectiveAngle = props.straight ? 0 : CARD_ANGLE_DEG;
    const effectiveAngleRad = (Math.abs(effectiveAngle) * Math.PI) / 180;
    const effRotatedW = Math.cos(effectiveAngleRad) * cardWidth + Math.sin(effectiveAngleRad) * cardHeight;
    const effRotatedH = Math.sin(effectiveAngleRad) * cardWidth + Math.cos(effectiveAngleRad) * cardHeight;

    const canvasW = effRotatedW + 2 * CANVAS_PADDING;
    const canvasH = effRotatedH + 2 * CANVAS_PADDING + 20;
    const design = sizeCanvas(canvasW, canvasH);

    const cardCx = canvasW / 2;
    const cardCy = canvasH / 2;

    if (!props.straight) {
      ctx.save();
      ctx.translate(cardCx - 2, cardCy + 10);
      ctx.rotate((-8 * Math.PI) / 180);
      drawRoundedRect(ctx, -cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, CARD_RADIUS);
      ctx.fillStyle = '#1a1a1a';
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(cardCx, cardCy);
    if (!props.straight) {
      ctx.rotate((CARD_ANGLE_DEG * Math.PI) / 180);
    }
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = 16;
    ctx.shadowOffsetX = 4;
    ctx.shadowOffsetY = 6;
    drawCardWithContent(ctx, cardWidth, cardHeight, modules);
    ctx.shadowColor = 'transparent';
    ctx.restore();
    return design;
  }

  const textBlockH = TEXT_GAP + 20 + 8 + 14;
  const titleBand = props.handshake ? HANDSHAKE_TITLE_BAND : 0;
  const canvasSize =
    Math.max(rotatedCardW + 2 * CANVAS_PADDING, cardHeight - TOP_OVERFLOW + textBlockH + CANVAS_PADDING) + titleBand;
  const design = sizeCanvas(canvasSize, canvasSize);

  const outerRadius = props.handshake ? canvasSize * SHEET_RADIUS_RATIO : 16;
  drawRoundedRect(ctx, 0, 0, canvasSize, canvasSize, preview ? outerRadius : 0);
  ctx.clip();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  const cardScale = props.handshake ? HANDSHAKE_CARD_SCALE : 1;
  const drawnCardH = cardHeight * cardScale;
  const cardCx = canvasSize / 2;
  const cardCy = drawnCardH / 2 - (props.handshake ? HANDSHAKE_TOP_OVERFLOW : TOP_OVERFLOW) + titleBand;

  if (props.handshake && preview) {
    drawHandshakeTitle(ctx, props.spotName, canvasSize, HANDSHAKE_TITLE_TOP);
    renderBack(canvasSize);
    void renderPlayBack(canvasSize);
  } else if (props.handshake) {
    drawHandshakeTitle(ctx, props.spotName, canvasSize, HANDSHAKE_TITLE_TOP);
  }

  ctx.save();
  ctx.translate(cardCx - 2, cardCy + 10);
  ctx.rotate((-8 * Math.PI) / 180);
  ctx.scale(cardScale, cardScale);
  drawRoundedRect(ctx, -cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, CARD_RADIUS);
  ctx.fillStyle = '#1a1a1a';
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.translate(cardCx, cardCy);
  ctx.rotate((CARD_ANGLE_DEG * Math.PI) / 180);
  ctx.scale(cardScale, cardScale);
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 16;
  ctx.shadowOffsetX = 4;
  ctx.shadowOffsetY = 6;
  drawCardWithContent(ctx, cardWidth, cardHeight, modules);
  ctx.shadowColor = 'transparent';
  ctx.restore();

  ctx.textAlign = 'center';

  if (props.handshake) {
    const lineWidth = 140;
    const lineStart = canvasSize / 2 - lineWidth / 2;
    const urlY = canvasSize - 14;
    const lineY = canvasSize - 36;
    const tilt = (Math.abs(CARD_ANGLE_DEG) * Math.PI) / 180;
    const cardBottom = cardCy + (drawnCardH / 2) * (Math.cos(tilt) + Math.sin(tilt));

    drawHandshakeBadge(ctx, lineStart - 8 - HANDSHAKE_BADGE_SIZE / 2, (cardBottom + lineY) / 2, HANDSHAKE_BADGE_SIZE);

    const noteSize = canvasSize * COVER.kindFont;
    const noteTracking = noteSize * 0.06;
    ctx.font = `500 ${noteSize}px 'Hanken Grotesk', sans-serif`;
    ctx.letterSpacing = `${noteTracking}px`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // Solid grey, lighter than gambits.com under it, so the URL stays the darker line.
    ctx.fillStyle = '#8A8A8A';
    ctx.fillText(localize('master.qr.print.note'), lineStart + lineWidth / 2 + noteTracking / 2, lineY - 8);
    ctx.letterSpacing = '0px';

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(lineStart, lineY);
    ctx.lineTo(lineStart + lineWidth, lineY);
    ctx.stroke();

    ctx.font = "12px 'Hanken Grotesk', sans-serif";
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillText('gambits.com', canvasSize / 2, urlY);
    return design;
  }

  const textY = cardCy + drawnCardH / 2 + TEXT_GAP;
  ctx.fillStyle = '#000000';
  ctx.font = "bold 17px 'Hanken Grotesk', sans-serif";
  ctx.fillText(props.spotName, canvasSize / 2, textY + 16);

  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.font = "12px 'Hanken Grotesk', sans-serif";
  ctx.fillText('gambits.com', canvasSize / 2, textY + 16 + 8 + 14);
  return design;
}

/** Big across the top. Returns where it landed so the words under it can line up. */
function drawHandshakeTitle(ctx: CanvasRenderingContext2D, title: string, canvasSize: number, top: number) {
  const maxWidth = canvasSize - 2 * HANDSHAKE_TITLE_INSET;
  ctx.font = `800 ${HANDSHAKE_TITLE_SIZE}px 'Hanken Grotesk', sans-serif`;
  const size = Math.min(HANDSHAKE_TITLE_SIZE, (HANDSHAKE_TITLE_SIZE * maxWidth) / ctx.measureText(title).width);
  ctx.font = `800 ${size}px 'Hanken Grotesk', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const metrics = ctx.measureText(title);
  const half = metrics.width / 2;
  const gradient = ctx.createLinearGradient(canvasSize / 2 - half, 0, canvasSize / 2 + half, 0);
  gradient.addColorStop(0, HANDSHAKE_TITLE_PINKS[0]);
  gradient.addColorStop(1, HANDSHAKE_TITLE_PINKS[1]);
  ctx.fillStyle = gradient;
  const baseline = top + metrics.actualBoundingBoxAscent;
  ctx.fillText(title, canvasSize / 2, baseline);
  return { fontSize: size, width: metrics.width, baseline };
}

/**
 * In shares of the square: crown, Play / Unplayed, 960 X 960 and the two words
 * under it, as one group. It sits a little above centre because gambits.com
 * takes the bottom corner.
 */
const COVER = {
  crown: 0.13,
  gap: 0.04,
  playFont: 0.08,
  playLine: 1.15,
  countGap: 0,
  countFont: 0.16,
  countLine: 0.9,
  countWidth: 0.84,
  kindFont: 0.036,
  kindGap: 0.35,
  lift: 0.04,
};

function drawCoverStack(ctx: CanvasRenderingContext2D, size: number) {
  const playLine = COVER.playFont * COVER.playLine;
  const countLine = COVER.countFont * COVER.countLine;
  const kindLine = COVER.kindFont * (1 + COVER.kindGap);
  const height = COVER.crown + COVER.gap + playLine * 2 + COVER.gap + COVER.countGap + countLine + kindLine;
  let y = ((1 - height) / 2 - COVER.lift) * size;

  drawCrownLogo(ctx, size / 2, y + (COVER.crown * size) / 2);
  y += (COVER.crown + COVER.gap) * size;

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const playSize = COVER.playFont * size;
  const tracking = playSize * 0.04;
  ctx.font = `800 ${playSize}px 'Hanken Grotesk', sans-serif`;
  ctx.letterSpacing = `${tracking}px`;
  ctx.textAlign = 'left';
  const playLines = [
    [{ key: 'master.cover.play', color: '#ffffff' }],
    [
      { key: 'auth.landing.unplayed.accent', color: HANDSHAKE_TITLE_PINKS[0] },
      { key: 'auth.landing.unplayed.rest', color: '#ffffff' },
    ],
  ];
  for (const parts of playLines) {
    const texts = parts.map((part) => localize(part.key).toUpperCase());
    const width = texts.reduce((sum, text) => sum + ctx.measureText(text).width, 0);
    let x = size / 2 - (width - tracking) / 2;
    parts.forEach((part, index) => {
      ctx.fillStyle = part.color;
      ctx.fillText(texts[index], x, y + (playLine * size) / 2);
      x += ctx.measureText(texts[index]).width;
    });
    y += playLine * size;
  }
  ctx.textAlign = 'center';
  y += (COVER.gap + COVER.countGap) * size;

  const countSize = COVER.countFont * size;
  ctx.letterSpacing = '0px';
  ctx.font = `800 ${countSize}px 'Hanken Grotesk', sans-serif`;
  const rowWidth = COVER.countWidth * size;
  const left = (size - rowWidth) / 2;
  const crossWidth = ctx.measureText(localize('auth.landing.master.times')).width;
  const column = (rowWidth - crossWidth) / 2;
  const firstCenter = left + column / 2;
  const secondCenter = left + column + crossWidth + column / 2;
  const countY = y + (countLine * size) / 2;
  const gradient = ctx.createLinearGradient(left, 0, left + rowWidth, 0);
  gradient.addColorStop(0, HANDSHAKE_TITLE_PINKS[0]);
  gradient.addColorStop(1, HANDSHAKE_TITLE_PINKS[1]);

  ctx.fillStyle = gradient;
  ctx.fillText(localize('master.story.road'), firstCenter, countY);
  ctx.fillText(localize('master.story.road'), secondCenter, countY);
  ctx.fillStyle = HANDSHAKE_TITLE_PINKS[0];
  ctx.fillText(localize('auth.landing.master.times'), size / 2, countY);
  y += countLine * size;

  const kindSize = COVER.kindFont * size;
  const kindTracking = kindSize * 0.06;
  ctx.font = `500 ${kindSize}px 'Hanken Grotesk', sans-serif`;
  ctx.letterSpacing = `${kindTracking}px`;
  ctx.fillStyle = '#F2F2F2';
  const kindY = y + kindSize * COVER.kindGap + kindSize / 2;
  ctx.fillText(localize('master.cover.positions'), firstCenter + kindTracking / 2, kindY);
  ctx.fillText(localize('master.cover.people'), secondCenter + kindTracking / 2, kindY);
  ctx.restore();
}

function drawSheetBoard(ctx: CanvasRenderingContext2D, size: number, squares = SHEET_SQUARES) {
  const square = size / BOARD_SQUARES;
  for (let row = 0; row < BOARD_SQUARES; row++) {
    for (let col = 0; col < BOARD_SQUARES; col++) {
      ctx.fillStyle = squares[(row + col) % 2 === 1 ? 0 : 1];
      ctx.fillRect(col * square, row * square, square + 0.5, square + 0.5);
    }
  }
}

/** The master crown: pink on a disc strong enough to read in ink. */
function drawCrownLogo(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.beginPath();
  ctx.arc(cx, cy, BACK_CROWN_DISC / 2, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(240, 75, 138, 0.32)';
  ctx.fill();

  ctx.save();
  ctx.translate(cx - BACK_CROWN_SIZE / 2, cy - BACK_CROWN_SIZE / 2);
  ctx.scale(BACK_CROWN_SIZE / CROWN_VIEWBOX, BACK_CROWN_SIZE / CROWN_VIEWBOX);
  ctx.fillStyle = HANDSHAKE_TITLE_PINKS[0];
  for (const path of CROWN_PATHS) ctx.fill(new Path2D(path));
  ctx.restore();
}

/** The other side of the print: the sheet's dark board, the crown, the count, and Play unplayed. */
function renderBack(size: number, canvas = backCanvasRef.value, scale = PREVIEW_SCALE) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const preview = canvas === backCanvasRef.value;

  canvas.width = Math.round(size * scale);
  canvas.height = Math.round(size * scale);
  if (preview) canvas.style.width = `${fittedWidth(size)}px`;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  drawRoundedRect(ctx, 0, 0, size, size, preview ? size * SHEET_RADIUS_RATIO : 0);
  ctx.clip();
  drawSheetBoard(ctx, size);

  drawCoverStack(ctx, size);
  drawBackUrl(ctx, size);
  return { width: size, height: size };
}

/**
 * The second back: the auth sheet on paper. The passerby is the open seat on
 * top, Play is the first word of the title, and the count labels the two
 * halves it crosses, people above and positions below. In shares of the square.
 */
const PLAY_BACK = {
  position: 29,
  inset: 0.06,
  seat: 0.12,
  seatRing: 0.009,
  seatIcon: 0.5,
  peopleGap: 0.035,
  disc: 0.24,
  titleGap: 0.025,
  rankBottom: 0.06,
  lineGap: 0.03,
  numberFont: 0.039,
};
/** The app's empty pawn, on its 16px box. */
const PAWN_EMPTY_PATHS = [
  'M13.7773 13.5466H2.22266V14.3904H13.7773V13.5466Z',
  'M13.7773 12.6787L13.7773 11.703L13.7656 11.7029L13.761 11.7029C12.0002 11.6868 10.5742 9.08428 10.5625 5.86805V5.82818H5.43759C5.43759 9.06274 4.00707 11.6868 2.23905 11.7029L2.23443 11.7029H2.22266V12.6787M4.45883 11.7029H11.5412C11.1817 11.3452 10.8772 10.9185 10.6307 10.4694C10.0819 9.46953 9.72266 8.20501 9.60482 6.82818H6.39518C6.27791 8.19832 5.92161 9.45682 5.37773 10.454C5.12962 10.9089 4.82226 11.3412 4.45883 11.7029ZM2.22266 14.3904H13.7773V13.5466H2.22266V14.3904Z',
  'M7.99991 3.98446C8.6557 3.98446 9.18733 3.45283 9.18733 2.79704C9.18733 2.14125 8.6557 1.60962 7.99991 1.60962C7.34411 1.60962 6.81249 2.14125 6.81249 2.79704C6.81249 3.45283 7.34411 3.98446 7.99991 3.98446ZM10.1873 2.79704C10.1873 4.00512 9.20799 4.98446 7.99991 4.98446C6.79183 4.98446 5.81249 4.00512 5.81249 2.79704C5.81249 1.58896 6.79183 0.609619 7.99991 0.609619C9.20799 0.609619 10.1873 1.58896 10.1873 2.79704Z',
];
const PAWN_EMPTY_VIEWBOX = 16;
/** The rank's browns, as the recommendation draws them. */
const RANK_SQUARES = ['#b58863', '#f0d9b5'];

/* The piece files carry a viewBox but no size, which Safari and Firefox will not
   rasterise onto a canvas, so the markup is sized and handed back as a data URL. */
async function loadPieceImage(piece: string, size: number): Promise<HTMLImageElement | null> {
  try {
    const response = await fetch(CHESS_PIECE_SVGS[piece]);
    if (!response.ok) return null;
    const markup = (await response.text()).replace('<svg', `<svg width="${size}" height="${size}"`);
    const image = new Image();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    await image.decode();
    return image;
  } catch {
    return null;
  }
}

/** The kind words of the first back: positions and people print in this one voice. */
function setKindFont(ctx: CanvasRenderingContext2D, size: number): number {
  const kindSize = size * COVER.kindFont;
  const tracking = kindSize * 0.06;
  ctx.font = `500 ${kindSize}px 'Hanken Grotesk', sans-serif`;
  ctx.letterSpacing = `${tracking}px`;
  ctx.fillStyle = '#F2F2F2';
  return tracking;
}

function drawOpenSeat(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  const radius = (PLAY_BACK.seat * size) / 2;
  ctx.beginPath();
  ctx.arc(cx, cy, radius + PLAY_BACK.seatRing * size, 0, 2 * Math.PI);
  ctx.fillStyle = HANDSHAKE_TITLE_PINKS[0];
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  ctx.fillStyle = '#000000';
  ctx.fill();

  const icon = radius * 2 * PLAY_BACK.seatIcon;
  ctx.save();
  ctx.translate(cx - icon / 2, cy - icon / 2);
  ctx.scale(icon / PAWN_EMPTY_VIEWBOX, icon / PAWN_EMPTY_VIEWBOX);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  for (const path of PAWN_EMPTY_PATHS) ctx.fill(new Path2D(path), 'evenodd');
  ctx.restore();
}

/** The recommendation's Play disc is 108px with an 18px label; the print keeps those shares. */
const RAISED_DISC_SIZE = 108;
const RAISED_DISC_LABEL = 18 / RAISED_DISC_SIZE;

/**
 * The recommendation's Play: the print pink at 45% over the darkest board grey,
 * raised like the app's disc, with its drop shadow, top edge light, bottom shade and sheen.
 */
function drawRaisedDisc(ctx: CanvasRenderingContext2D, cx: number, cy: number, radius: number) {
  const k = (radius * 2) / RAISED_DISC_SIZE;
  const circle = () => {
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  };

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = 28 * k;
  ctx.shadowOffsetY = 12 * k;
  circle();
  ctx.fillStyle = SHEET_SQUARES[0];
  ctx.fill();
  ctx.restore();

  circle();
  ctx.fillStyle = 'rgba(240, 75, 138, 0.45)';
  ctx.fill();

  ctx.save();
  circle();
  ctx.clip();

  const shade = ctx.createLinearGradient(0, cy + radius - 26 * k, 0, cy + radius);
  shade.addColorStop(0, 'rgba(0, 0, 0, 0)');
  shade.addColorStop(1, 'rgba(0, 0, 0, 0.3)');
  ctx.fillStyle = shade;
  ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);

  ctx.save();
  ctx.translate(cx, cy - radius + radius * 2 * 0.08);
  ctx.scale(radius * 2 * 1.1, radius * 2 * 0.8);
  const sheen = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
  sheen.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
  sheen.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = sheen;
  ctx.fillRect(-1, -1, 2, 2);
  ctx.restore();

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  ctx.arc(cx, cy + 2 * k, radius, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.fill('evenodd');
  ctx.restore();
}

/** How far the pair's second face slides over the first, as in the app's overlapping faces. */
const PAIR_OVERLAP = 0.2;
/** The app's second face carries a 2px dark edge on its 44px circle, so it stands off the first. */
const PAIR_EDGE = 2 / 44;

/** The first back's crown in its pink circle, as the second face of the pair. */
function drawCrownFace(ctx: CanvasRenderingContext2D, cx: number, cy: number, diameter: number) {
  const radius = diameter / 2;
  ctx.beginPath();
  ctx.arc(cx, cy, radius + diameter * PAIR_EDGE, 0, 2 * Math.PI);
  ctx.fillStyle = SHEET_SQUARES[0];
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  ctx.fillStyle = 'rgba(240, 75, 138, 0.32)';
  ctx.fill();

  const crown = diameter * (BACK_CROWN_SIZE / BACK_CROWN_DISC);
  ctx.save();
  ctx.translate(cx - crown / 2, cy - crown / 2);
  ctx.scale(crown / CROWN_VIEWBOX, crown / CROWN_VIEWBOX);
  ctx.fillStyle = HANDSHAKE_TITLE_PINKS[0];
  for (const path of CROWN_PATHS) ctx.fill(new Path2D(path));
  ctx.restore();
}

/** Play on the board's middle line, as on the recommendation, then the rest of the title under it, Un in pink. */
function drawPlayTitle(ctx: CanvasRenderingContext2D, size: number) {
  const radius = (PLAY_BACK.disc * size) / 2;
  const discCy = size / 2;
  drawRaisedDisc(ctx, size / 2, discCy, radius);

  const labelSize = radius * 2 * RAISED_DISC_LABEL;
  const labelTracking = labelSize * 0.06;
  const label = localize('handshake.game.play').toUpperCase();
  ctx.font = `700 ${labelSize}px 'Hanken Grotesk', sans-serif`;
  ctx.letterSpacing = `${labelTracking}px`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ffffff';
  const metrics = ctx.measureText(label);
  const baseline = discCy + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;
  ctx.fillText(label, size / 2 + labelTracking / 2, baseline);

  ctx.textBaseline = 'middle';

  const playSize = COVER.playFont * size;
  const tracking = playSize * 0.04;
  ctx.font = `800 ${playSize}px 'Hanken Grotesk', sans-serif`;
  ctx.letterSpacing = `${tracking}px`;
  ctx.textAlign = 'left';
  const parts = [
    { text: localize('auth.landing.unplayed.accent').toUpperCase(), color: HANDSHAKE_TITLE_PINKS[0] },
    { text: localize('auth.landing.unplayed.rest').toUpperCase(), color: '#ffffff' },
  ];
  const width = parts.reduce((sum, part) => sum + ctx.measureText(part.text).width, 0);
  let x = size / 2 - (width - tracking) / 2;
  const y = discCy + radius + PLAY_BACK.titleGap * size + (playSize * COVER.playLine) / 2;
  for (const part of parts) {
    ctx.fillStyle = part.color;
    ctx.fillText(part.text, x, y);
    x += ctx.measureText(part.text).width;
  }
}

/** The position line, mirroring the people under the faces, and white's rank, a to h, along the bottom. */
async function drawPlayRank(ctx: CanvasRenderingContext2D, size: number, scale: number) {
  const left = PLAY_BACK.inset * size;
  const width = size - 2 * left;
  const square = width / BOARD_SQUARES;
  const top = size - PLAY_BACK.rankBottom * size - square;
  const pieces = backRankFromNumber(PLAY_BACK.position);
  const pieceSize = square * 0.82;
  const images = await Promise.all(pieces.map((piece) => loadPieceImage(piece, Math.round(pieceSize * scale))));

  ctx.save();
  drawRoundedRect(ctx, left, top, width, square, square * 0.12);
  ctx.clip();
  pieces.forEach((_, file) => {
    ctx.fillStyle = RANK_SQUARES[file % 2];
    ctx.fillRect(left + file * square, top, square + 0.5, square);
  });
  ctx.restore();

  images.forEach((image, file) => {
    if (!image) return;
    const offset = (square - pieceSize) / 2;
    ctx.drawImage(image, left + file * square + offset, top + offset, pieceSize, pieceSize);
  });

  const lineY = top - PLAY_BACK.lineGap * size;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.letterSpacing = '0px';
  const numberSize = PLAY_BACK.numberFont * size;
  ctx.font = `600 ${numberSize}px 'Hanken Grotesk', sans-serif`;
  ctx.fillStyle = '#F2F2F2';
  const number = `#${PLAY_BACK.position}`;
  ctx.fillText(number, left, lineY);
  let x = left + ctx.measureText(number).width + numberSize * 0.45;
  ctx.fillStyle = '#C8C8C8';
  ctx.fillText('·', x, lineY);
  x += ctx.measureText('·').width + numberSize * 0.45;
  setKindFont(ctx, size);
  ctx.fillText(`${localize('master.story.road')} ${localize('master.cover.positions')}`, x, lineY);
}

async function renderPlayBack(size: number, canvas = playBackCanvasRef.value, scale = PREVIEW_SCALE) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const preview = canvas === playBackCanvasRef.value;

  canvas.width = Math.round(size * scale);
  canvas.height = Math.round(size * scale);
  if (preview) canvas.style.width = `${fittedWidth(size)}px`;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  drawRoundedRect(ctx, 0, 0, size, size, preview ? size * SHEET_RADIUS_RATIO : 0);
  ctx.clip();
  drawSheetBoard(ctx, size);

  const seatCy = PLAY_BACK.inset * size + (PLAY_BACK.seat * size) / 2;
  const pairShift = PLAY_BACK.seat * size * (1 - PAIR_OVERLAP) * 0.5;
  drawOpenSeat(ctx, size / 2 - pairShift, seatCy, size);
  drawCrownFace(ctx, size / 2 + pairShift, seatCy, PLAY_BACK.seat * size);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const peopleTracking = setKindFont(ctx, size);
  const peopleY = seatCy + (PLAY_BACK.seat * size) / 2 + PLAY_BACK.peopleGap * size;
  ctx.fillText(
    `${localize('master.story.road')} ${localize('master.cover.people')}`,
    size / 2 + peopleTracking / 2,
    peopleY,
  );

  drawPlayTitle(ctx, size);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.letterSpacing = '0px';
  await drawPlayRank(ctx, size, scale);
  return { width: size, height: size };
}

function drawBackUrl(ctx: CanvasRenderingContext2D, size: number) {
  ctx.font = "12px 'Hanken Grotesk', sans-serif";
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#C8C8C8';
  ctx.fillText('gambits.com', size / 2, size - BACK_URL_INSET);
}

/**
 * The profile handshake button: four squares of the sheet checker, the empty
 * hand in white. The conic on the profile starts at the top and runs clockwise.
 */
function drawHandshakeBadge(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  const radius = size * HANDSHAKE_MARK_RADIUS;
  const half = size / 2;
  const x = cx - half;
  const y = cy - half;

  ctx.save();
  drawRoundedRect(ctx, x, y, size, size, radius);
  ctx.clip();
  const squares: Array<[number, number, string]> = [
    [half, 0, SHEET_SQUARES[0]],
    [half, half, SHEET_SQUARES[1]],
    [0, half, SHEET_SQUARES[0]],
    [0, 0, SHEET_SQUARES[1]],
  ];
  for (const [sx, sy, color] of squares) {
    ctx.fillStyle = color;
    ctx.fillRect(x + sx, y + sy, half + 0.5, half + 0.5);
  }
  ctx.restore();

  drawRoundedRect(ctx, x, y, size, size, radius);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  drawHandshakeIcon(ctx, cx, cy, size * HANDSHAKE_MARK_ICON, '#ffffff');
}

function saveCanvas(canvas: HTMLCanvasElement | undefined, suffix: string) {
  if (!canvas) return;
  const link = document.createElement('a');
  link.download = `${fileName(suffix)}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

const fileName = (suffix: string) => `${props.spotName.replace(/[^a-zA-Z0-9]/g, '-')}-${suffix}`;

/** Transparent pixels land on white, which is the paper the page is printed on. */
function canvasRgb(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext('2d');
  if (!ctx) return new Uint8Array();
  const { width, height } = canvas;
  const pixels = ctx.getImageData(0, 0, width, height).data;
  const rgb = new Uint8Array(width * height * 3);
  for (let i = 0, j = 0; i < pixels.length; i += 4, j += 3) {
    const alpha = pixels[i + 3];
    rgb[j] = Math.round((pixels[i] * alpha + 255 * (255 - alpha)) / 255);
    rgb[j + 1] = Math.round((pixels[i + 1] * alpha + 255 * (255 - alpha)) / 255);
    rgb[j + 2] = Math.round((pixels[i + 2] * alpha + 255 * (255 - alpha)) / 255);
  }
  return rgb;
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** One page, the artwork filling it. The image is lossless, so the QR stays exact. */
async function canvasToPdf(canvas: HTMLCanvasElement, pageW: number, pageH: number): Promise<Uint8Array> {
  const image = await deflate(canvasRgb(canvas));
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  let length = 0;
  const offsets: number[] = [];
  const push = (bytes: Uint8Array) => {
    chunks.push(bytes);
    length += bytes.length;
  };
  const pushText = (text: string) => push(encoder.encode(text));
  const startObject = (index: number) => {
    offsets[index] = length;
    pushText(`${index} 0 obj\n`);
  };

  const width = pageW.toFixed(2);
  const height = pageH.toFixed(2);
  const content = `q\n${width} 0 0 ${height} 0 0 cm\n/Im Do\nQ\n`;

  pushText('%PDF-1.4\n');
  push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));
  startObject(1);
  pushText('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
  startObject(2);
  pushText('<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');
  startObject(3);
  pushText(
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${width} ${height}] /Contents 4 0 R /Resources << /XObject << /Im 5 0 R >> >> >>\nendobj\n`,
  );
  startObject(4);
  pushText(`<< /Length ${content.length} >>\nstream\n${content}endstream\nendobj\n`);
  startObject(5);
  pushText(
    `<< /Type /XObject /Subtype /Image /Width ${canvas.width} /Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode /Length ${image.length} >>\nstream\n`,
  );
  push(image);
  pushText('endstream\nendobj\n');

  const xref = length;
  pushText(`xref\n0 6\n0000000000 65535 f \n`);
  for (let index = 1; index <= 5; index++) pushText(`${String(offsets[index]).padStart(10, '0')} 00000 n \n`);
  pushText(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`);

  const pdf = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, offset);
    offset += chunk.length;
  }
  return pdf;
}

async function exportPdf(
  suffix: string,
  draw: (
    canvas: HTMLCanvasElement,
  ) => Promise<{ width: number; height: number } | undefined> | { width: number; height: number } | undefined,
) {
  if (pdfBusy.value) return;
  pdfBusy.value = true;
  try {
    const canvas = document.createElement('canvas');
    const design = await draw(canvas);
    if (!design) return;
    const pdf = await canvasToPdf(canvas, design.width, design.height);
    const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.download = `${fileName(suffix)}.pdf`;
    link.href = url;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } finally {
    pdfBusy.value = false;
  }
}

function saveFrontPdf() {
  return exportPdf('qr', (canvas) => renderCanvas(qrModules, canvas, PRINT_SCALE));
}

function saveBackPdf() {
  return exportPdf('qr-back', (canvas) => renderBack(sheetSize(), canvas, PRINT_SCALE));
}

function savePlayBackPdf() {
  return exportPdf('qr-back-play', (canvas) => renderPlayBack(sheetSize(), canvas, PRINT_SCALE));
}

/** The square the two backs share with the front. */
function sheetSize(): number {
  const cardSize = BOARD_SIZE + BOARD_BORDER * 2 + COORD_GAP * 2 + CARD_PADDING * 2;
  const tilt = (Math.abs(CARD_ANGLE_DEG) * Math.PI) / 180;
  const rotated = Math.cos(tilt) * cardSize + Math.sin(tilt) * cardSize;
  const textBlockH = TEXT_GAP + 20 + 8 + 14;
  const titleBand = props.handshake ? HANDSHAKE_TITLE_BAND : 0;
  return Math.max(rotated + 2 * CANVAS_PADDING, cardSize - TOP_OVERFLOW + textBlockH + CANVAS_PADDING) + titleBand;
}

onMounted(() => {
  const spotUrl = props.url ?? `${window.location.origin}/spot/${props.spotId}`;
  const qrData = QRCode.create(spotUrl, { errorCorrectionLevel: 'M' });
  const size = qrData.modules.size;
  const data = qrData.modules.data;

  const modules: boolean[][] = [];
  for (let row = 0; row < size; row++) {
    modules[row] = [];
    for (let col = 0; col < size; col++) {
      modules[row][col] = data[row * size + col] === 1;
    }
  }

  qrModules = modules;
  renderCanvas(modules);
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.spot-qr-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  position: relative;
  z-index: 1;
}

.qr-canvas {
  // The canvas is drawn at a fixed pixel size, so it has to scale down rather
  // than push the dialog panel past the 88vw the modal allows on mobile.
  max-width: 100%;
  height: auto;
  border-radius: 0.75rem;

  &.is-square {
    border-radius: 0;
  }
}

.qr-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.qr-download-btn {
  background: transparent;
  border: 1px solid var(--color-white-10);
  color: var(--color-white-70);
  border-radius: 0.5rem;
  padding: 0.5rem 1.25rem;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 150ms ease;

  @include hover {
    background: var(--color-white-5);
    color: var(--color-white);
    border-color: var(--color-white-30);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;

    @include hover {
      background: transparent;
      color: var(--color-white-70);
      border-color: var(--color-white-10);
    }
  }
}
</style>
